
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { AutomationConfigModel } from '@/models/automation_config';
import dateFormat from 'dateformat';

/**
 * A component for selecting a schedule (hourly, daily, weekly) for an automation.
 * It dynamically displays fields based on the selected schedule type and
 * provides a preview of the next scheduled run time.
 * @emits input - The updated AutomationConfigModel object.
 */
@Component
export default class SchedulePicker extends Vue {
  /**
   * The configuration object containing the schedule details.
   * Passed as a `v-model`.
   * @type {AutomationConfigModel}
   */
  @Prop({ type: Object, required: true }) value!: AutomationConfigModel;

  /**
   * Validation rules for the form fields.
   */
  private rules = {
    required: (v: any) => !!v || v === 0 || 'This field is required',
  };

  /**
   * The options for the schedule type selector.
   */
  private scheduleTypeOptions = [
    { text: 'Daily', value: 'daily' },
    { text: 'Weekly', value: 'weekly' },
    { text: 'Monthly', value: 'monthly' },
  ];

  /**
   * Gets the scheduleConfig object from the v-model prop.
   * @returns {object} The schedule configuration.
   */
  get scheduleConfig() {
    return this.value.scheduleConfig || {};
  }

  /**
   * Generates options for the hour selector (0-23).
   * @returns {object[]} An array of hour options.
   */
  get hourOptions() {
    return Array.from({ length: 24 }, (_, i) => ({ 
      text: `${String(i).padStart(2, '0')}:00`, 
      value: i 
    }));
  }

  /**
   * Generates options for the day of the week selector (0-6).
   * @returns {object[]} An array of day of the week options.
   */
  get dayOfWeekOptions() {
    return [
      { text: 'Sunday', value: 0 },
      { text: 'Monday', value: 1 },
      { text: 'Tuesday', value: 2 },
      { text: 'Wednesday', value: 3 },
      { text: 'Thursday', value: 4 },
      { text: 'Friday', value: 5 },
      { text: 'Saturday', value: 6 },
    ];
  }

  /**
   * Generates options for the day of the month selector (1-31 and -1 for last day).
   * @returns {object[]} An array of day of the month options.
   */
  get dayOfMonthOptions() {
    const options = Array.from({ length: 31 }, (_, i) => ({
      text: String(i + 1),
      value: i + 1,
    }));
    options.push({ text: 'Last day of month', value: -1 });
    return options;
  }

  /**
   * Gets the user's local time zone.
   * @returns {string} The IANA time zone name.
   */
  get timeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /**
   * Calculates and formats a preview of the next run time based on the current schedule settings.
   * @returns {string} A formatted string of the next run time, or an empty string if not applicable.
   */
  get nextRunPreview(): string {
    if (!this.value.scheduleType) return '';
    
    const now = new Date();
    let nextRun = new Date();
    
    switch (this.value.scheduleType) {
      case 'daily':
        if (this.scheduleConfig.hour !== undefined) {
          nextRun.setHours(this.scheduleConfig.hour);
          nextRun.setMinutes(0);
          nextRun.setSeconds(0);
          if (nextRun <= now) {
            nextRun.setDate(nextRun.getDate() + 1);
          }
        }
        break;
        
      case 'weekly':
        if (this.scheduleConfig.dayOfWeek !== undefined && 
            this.scheduleConfig.hour !== undefined) {
          const daysUntilTarget = (this.scheduleConfig.dayOfWeek - now.getDay() + 7) % 7;
          nextRun.setDate(now.getDate() + (daysUntilTarget || 7));
          nextRun.setHours(this.scheduleConfig.hour);
          nextRun.setMinutes(0);
          nextRun.setSeconds(0);
          
          if (daysUntilTarget === 0 && nextRun <= now) {
            nextRun.setDate(nextRun.getDate() + 7);
          }
        }
        break;
      
      case 'monthly':
        if (this.scheduleConfig.dayOfMonth !== undefined && 
            this.scheduleConfig.hour !== undefined) {
          
          const setRunDate = (date: Date) => {
            if (this.scheduleConfig.dayOfMonth === -1) {
              // Last day of month
              const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);
              return new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 0);
            } else {
              return new Date(date.getFullYear(), date.getMonth(), this.scheduleConfig.dayOfMonth);
            }
          };

          nextRun = setRunDate(now);
          nextRun.setHours(this.scheduleConfig.hour, 0, 0, 0);

          if (nextRun <= now) {
            const nextMonthDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
            nextRun = setRunDate(nextMonthDate);
            nextRun.setHours(this.scheduleConfig.hour, 0, 0, 0);
          }
        }
        break;
    }
    
    return dateFormat(nextRun, 'mmm d, yyyy h:MM TT');
  }

  /**
   * Updates the schedule type and resets the schedule config.
   * Emits the updated value for the v-model.
   * @param {string} type - The new schedule type.
   */
  private updateScheduleType(type: string) {
    const updatedConfig = {
      ...this.value,
      scheduleType: type,
      scheduleConfig: {} // Reset config when type changes
    };
    this.$emit('input', updatedConfig);
  }

  /**
   * Updates a specific field within the scheduleConfig object.
   * Emits the updated value for the v-model.
   * @param {string} field - The field to update in `scheduleConfig`.
   * @param {any} value - The new value for the field.
   */
  private updateScheduleConfig(field: string, value: any) {
    const updatedConfig = {
      ...this.value,
      scheduleConfig: {
        ...this.scheduleConfig,
        [field]: value
      }
    };
    this.$emit('input', updatedConfig);
  }
}
